/**
 * Rubrics Pages
 *
 * Read-only list and detail views for rubrics.
 */

import { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../lib/api';

import { RequestPanel, StatusBadge } from '../components/RequestPanel';
import { JsonViewer, Code } from '../components/JsonViewer';
import type { ApiResult, Rubric, RubricListResponse } from '../lib/types';

export function Rubrics() {
  const navigate = useNavigate();
  const [rubrics, setRubrics] = useState<Rubric[]>([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ApiResult<RubricListResponse> | null>(null);

  const loadRubrics = useCallback(async () => {
    setLoading(true);
    const res = await apiClient.listRubrics({ limit: 50 });
    setResult(res);
    if (res.response.data) {
      setRubrics(res.response.data.items);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadRubrics();
  }, [loadRubrics]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 30, fontWeight: 600, letterSpacing: '-0.025em', color: 'var(--slate-900)', marginBottom: 4 }}>
            Rubrics
          </h1>
          <p style={{ color: 'var(--slate-500)', fontSize: 14, margin: 0 }}>
            Read-only scoring rubrics for transcript evaluation
          </p>
        </div>
        <button className="btn btn-tertiary" onClick={loadRubrics} disabled={loading}>
          <iconify-icon icon="lucide:refresh-cw" width="16" height="16"></iconify-icon>
          Refresh
        </button>
      </div>

      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Version</th>
              <th>Status</th>
              <th>Passing Score</th>
              <th>Max Score</th>
            </tr>
          </thead>
          <tbody>
            {rubrics.map((r) => (
              <tr
                key={`${r.id}-${r.version}`}
                className="clickable"
                onClick={() => navigate(`/console/rubrics/${r.id}`)}
              >
                <td>
                  <Code>{r.id.slice(0, 12)}...</Code>
                </td>
                <td style={{ fontWeight: 500 }}>{r.name}</td>
                <td>{r.version}</td>
                <td>
                  <StatusBadge status={r.status} />
                </td>
                <td>{r.passingScore}</td>
                <td>{r.maxScore}</td>
              </tr>
            ))}
            {rubrics.length === 0 && !loading && (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', padding: 48 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                    <iconify-icon icon="lucide:scale" width="32" height="32" style={{ color: 'var(--slate-300)' }}></iconify-icon>
                    <span style={{ color: 'var(--slate-400)' }}>No rubrics found</span>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <RequestPanel result={result} loading={loading} />
    </div>
  );
}

export function RubricDetail() {
  const { id } = useParams<{ id: string }>();
  const [rubric, setRubric] = useState<Rubric | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ApiResult<Rubric> | null>(null);

  const loadRubric = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    const res = await apiClient.getRubric(id);
    setResult(res);
    if (res.response.data) {
      setRubric(res.response.data);
    }
    setLoading(false);
  }, [id]);

  useEffect(() => {
    loadRubric();
  }, [loadRubric]);

  return (
    <div>
      <Link
        to="/console/rubrics"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          color: 'var(--slate-500)',
          fontSize: 14,
          textDecoration: 'none',
          marginBottom: 16,
        }}
      >
        <iconify-icon icon="lucide:arrow-left" width="16" height="16"></iconify-icon>
        Back to Rubrics
      </Link>

      <h1 style={{ fontSize: 30, fontWeight: 600, letterSpacing: '-0.025em', color: 'var(--slate-900)', marginBottom: 24 }}>
        Rubric Detail
      </h1>

      {rubric && (
        <>
          <div className="card">
            <h2>Summary</h2>
            <table className="table" style={{ maxWidth: 600 }}>
              <tbody>
                <tr>
                  <td style={{ fontWeight: 500, width: 180 }}>ID</td>
                  <td><Code>{rubric.id}</Code></td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 500 }}>Name</td>
                  <td style={{ fontWeight: 500 }}>{rubric.name}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 500 }}>Version</td>
                  <td>{rubric.version}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 500 }}>Status</td>
                  <td><StatusBadge status={rubric.status} /></td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 500 }}>Passing Score</td>
                  <td>{rubric.passingScore}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 500 }}>Max Score</td>
                  <td>{rubric.maxScore}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 500 }}>Dimensions</td>
                  <td>{rubric.dimensions.length}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 500 }}>Created At</td>
                  <td>{new Date(rubric.createdAt).toLocaleString()}</td>
                </tr>
                {rubric.publishedAt && (
                  <tr>
                    <td style={{ fontWeight: 500 }}>Published At</td>
                    <td>{new Date(rubric.publishedAt).toLocaleString()}</td>
                  </tr>
                )}
              </tbody>
            </table>

            {rubric.description && (
              <div style={{ marginTop: 16 }}>
                <h3>Description</h3>
                <p className="text-muted">{rubric.description}</p>
              </div>
            )}
          </div>

          <div className="card">
            <h2>Dimensions</h2>
            {rubric.dimensions.map((dim, i) => (
              <div
                key={dim.id}
                style={{
                  marginBottom: 16,
                  paddingBottom: 16,
                  borderBottom: i < rubric.dimensions.length - 1 ? '1px solid #e5e5e5' : 'none',
                }}
              >
                <h3>
                  {dim.name}{' '}
                  <span className="text-muted text-small">(weight: {dim.weight})</span>
                </h3>
                {dim.description && (
                  <p className="text-muted text-small" style={{ marginBottom: 8 }}>
                    {dim.description}
                  </p>
                )}
                <table className="table" style={{ fontSize: 13 }}>
                  <thead>
                    <tr>
                      <th>Rule ID</th>
                      <th>Type</th>
                      <th>Pattern</th>
                      <th>Score</th>
                      <th>Required</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dim.rules.map((rule) => (
                      <tr key={rule.id}>
                        <td><Code>{rule.id}</Code></td>
                        <td>{rule.type}</td>
                        <td className="mono" style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {rule.pattern}
                        </td>
                        <td>{rule.score}</td>
                        <td>{rule.required ? 'Yes' : 'No'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>

          <div className="card">
            <h2>Full JSON</h2>
            <JsonViewer data={rubric} maxHeight={500} />
          </div>

          <div className="card">
            <h2>Actions</h2>
            <Link to={`/console/scores?rubricId=${rubric.id}`} className="btn btn-primary">
              Generate Score with this Rubric
            </Link>
          </div>
        </>
      )}

      <RequestPanel result={result} loading={loading} />
    </div>
  );
}
